import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";

interface EventItem {
  id: number;
  event_type: string;
  data: Record<string, unknown> | null;
  created_at: string;
  actor_name: string | null;
}

const EVENT_LABELS: Record<string, string> = {
  "candidate.screened": "Screened",
  "candidate.intake_completed": "Intake completed",
  "candidate.salary_benchmarked": "Salary benchmarked",
  "candidate.stage_changed": "Stage changed",
  "candidate.archived": "Archived",
  "candidate.deleted": "Deleted",
};

const EVENT_COLORS: Record<string, string> = {
  "candidate.screened": "bg-emerald-500",
  "candidate.intake_completed": "bg-blue-500",
  "candidate.salary_benchmarked": "bg-violet-500",
  "candidate.stage_changed": "bg-amber-500",
  "candidate.archived": "bg-gray-400",
};

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr + (dateStr.endsWith("Z") ? "" : "Z")).getTime();
  const seconds = Math.floor((now - then) / 1000);
  if (seconds < 60) return "Just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr + (dateStr.endsWith("Z") ? "" : "Z")).toLocaleDateString(undefined, {
    month: "short", day: "numeric",
  });
}

export function ActivityTimeline({ candidateId }: { candidateId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.events({ entityType: "candidate", entityId: candidateId }),
    queryFn: () => api.listEvents({ entityType: "candidate", entityId: candidateId, limit: 20 }),
  });

  const events = (data?.events ?? []) as EventItem[];

  if (isLoading) {
    return <p className="text-xs text-text-secondary">Loading activity...</p>;
  }

  if (events.length === 0) {
    return <p className="text-xs text-text-secondary">No activity recorded</p>;
  }

  return (
    <div className="space-y-2">
      <div className="text-[8px] font-bold uppercase tracking-[0.32px] text-text-secondary">
        Activity
      </div>
      <div className="space-y-0">
        {events.map((event, i) => {
          const dotColor = EVENT_COLORS[event.event_type] ?? "bg-gray-300";
          const label = EVENT_LABELS[event.event_type] ?? event.event_type;
          const stageData = event.data as { from?: string; to?: string; notes?: string } | null;

          return (
            <div key={event.id} className="flex gap-3">
              {/* Timeline line + dot */}
              <div className="flex flex-col items-center">
                <div className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${dotColor}`} />
                {i < events.length - 1 && <div className="w-px flex-1 bg-surface-secondary" />}
              </div>
              {/* Content */}
              <div className="pb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-near-black">{label}</span>
                  <span className="text-[10px] text-text-secondary">{timeAgo(event.created_at)}</span>
                </div>
                {event.event_type === "candidate.stage_changed" && stageData?.from && (
                  <p className="text-[11px] text-text-secondary">
                    {stageData.from} → {stageData.to}
                    {stageData.notes && ` — ${stageData.notes}`}
                  </p>
                )}
                {event.actor_name && (
                  <p className="text-[10px] text-text-secondary">by {event.actor_name}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
